import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Info, Globe } from 'lucide-react';
import { setCanonical, setMeta } from '../utils/seo';
import { BrandSchemas } from './SEOSchemas';
import { SponsorBanner, AdSensePlaceholder, ClickBankBanner } from './AdSlots';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REGIONS = ['us', 'uk', 'eu', 'jp', 'kr', 'au'];
const REGION_LABELS = { us: 'US', uk: 'UK', eu: 'EU', jp: 'Japan', kr: 'Korea', au: 'Australia' };

export default function BrandPage() {
  const { slug, subpath: urlCategory } = useParams();
  const [brand, setBrand] = useState(null);
  const [activeCat, setActiveCat] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/brands/${slug}`).then(r => {
      setBrand(r.data);
      if (urlCategory) {
        const idx = r.data.categories?.findIndex(c => c.slug === urlCategory);
        if (idx >= 0) setActiveCat(idx);
      }
      const catLabel = urlCategory ? ` - ${urlCategory.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}` : '';
      setCanonical(urlCategory ? `/brand/${slug}/${urlCategory}` : `/brand/${slug}`);
      setMeta(`${r.data.name} Size Chart${catLabel}`, `Complete ${r.data.name} size chart with US, UK, EU, Japan & Korea conversions. ${r.data.fit_guide}`);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [slug, urlCategory]);

  if (loading) return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-[#78716C]">Loading...</div>;
  if (!brand) return <div className="max-w-7xl mx-auto px-4 py-20 text-center"><h1 className="text-2xl font-bold mb-4">Brand Not Found</h1><Link to="/brands" className="text-[#C5A028]">Browse all brands</Link></div>;

  const cat = brand.categories?.[activeCat];
  const isShoes = cat?.slug?.includes('shoes');
  const isTops = cat?.slug?.includes('tops');
  const isPants = cat?.slug?.includes('pants');

  const measureCols = isShoes ? ['cm'] : isTops ? (cat.slug.includes('womens') ? ['bust_cm', 'waist_cm', 'hip_cm'] : ['chest_cm', 'waist_cm']) : isPants ? ['waist_cm', 'hip_cm'] : ['chest_cm'];
  const measureLabels = { chest_cm: 'Chest (cm)', chest_in: 'Chest (in)', bust_cm: 'Bust (cm)', waist_cm: 'Waist (cm)', hip_cm: 'Hip (cm)', cm: 'Length (cm)' };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
      <Link to="/brands" className="inline-flex items-center gap-2 text-sm text-[#78716C] hover:text-[#C5A028] mb-6 transition-colors" data-testid="back-to-brands"><ArrowLeft className="w-4 h-4" /> All Brands</Link>
      {brand && <BrandSchemas brand={brand} />}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-light tracking-tighter text-[#1C1917]" data-testid="brand-name">{brand.name}</h1>
          <span className="text-xs bg-[#FAF8F4] px-3 py-1 border border-[#E8E2D9] flex items-center gap-1"><Globe className="w-3 h-3" />{brand.country_origin}</span>
        </div>
        <p className="text-[#78716C] max-w-2xl mb-4">{brand.description}</p>
        <div className="bg-[#FAF8F4] border border-[#E8E2D9] p-4 flex gap-3 items-start">
          <Info className="w-5 h-5 text-[#C5A028] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-[#C5A028] mb-1">Fit Guide</p>
            <p className="text-sm text-[#78716C]">{brand.fit_guide}</p>
          </div>
        </div>
      </div>

      {/* Category Tabs */}
      {brand.categories?.length > 0 && (
        <>
          <div className="flex gap-2 flex-wrap mb-6">
            {brand.categories.map((c, i) => (
              <button key={c.slug} onClick={() => setActiveCat(i)} className={`px-4 py-2 text-sm font-medium border transition-colors ${activeCat === i ? 'bg-[#1C1917] text-white border-[#1C1917]' : 'bg-white text-[#78716C] border-[#E8E2D9] hover:border-[#C5A028]'}`} data-testid={`cat-tab-${c.slug}`}>{c.name}</button>
            ))}
          </div>

          {cat && (
            <div>
              {cat.fit_note && <p className="text-sm text-[#78716C] mb-4 italic">{cat.fit_note}</p>}
              <div className="overflow-x-auto border border-[#E8E2D9]">
                <table className="w-full text-sm" data-testid="size-chart-table">
                  <thead>
                    <tr className="bg-[#FAF8F4]">
                      {isShoes ? null : <th className="text-left p-3 font-semibold text-[#1C1917] border-b border-[#E8E2D9]">Size</th>}
                      {REGIONS.map(r => {
                        const hasRegion = cat.sizes?.some(s => s[r]);
                        return hasRegion ? <th key={r} className="text-left p-3 font-semibold text-[#1C1917] border-b border-[#E8E2D9]">{REGION_LABELS[r]}</th> : null;
                      })}
                      {measureCols.map(m => <th key={m} className="text-left p-3 font-semibold text-[#1C1917] border-b border-[#E8E2D9]">{measureLabels[m] || m}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {cat.sizes?.map((s, i) => (
                      <tr key={i} className="hover:bg-[#FAF8F4] transition-colors">
                        {isShoes ? null : <td className="p-3 font-medium text-[#1C1917] border-b border-[#E8E2D9]">{s.label}</td>}
                        {REGIONS.map(r => {
                          const hasRegion = cat.sizes?.some(sz => sz[r]);
                          return hasRegion ? <td key={r} className="p-3 text-[#78716C] border-b border-[#E8E2D9]">{s[r] || '-'}</td> : null;
                        })}
                        {measureCols.map(m => <td key={m} className="p-3 text-[#78716C] border-b border-[#E8E2D9]">{s[m] || '-'}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Cross-links */}
      <div className="mt-10 bg-[#FAF8F4] border border-[#E8E2D9] p-6">
        <h3 className="font-['Cormorant_Garamond'] text-xl font-semibold text-[#1C1917] mb-4">Related Tools</h3>
        <div className="flex flex-wrap gap-3">
          <Link to="/find-my-size" className="text-sm text-[#C5A028] hover:underline" data-testid="brand-to-quiz">Find My Size in {brand.name}</Link>
          <span className="text-[#E8E2D9]">|</span>
          <Link to="/compare" className="text-sm text-[#C5A028] hover:underline" data-testid="brand-to-compare">Compare {brand.name} with Other Brands</Link>
          <span className="text-[#E8E2D9]">|</span>
          <Link to="/brands" className="text-sm text-[#C5A028] hover:underline">Browse All Brands</Link>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="mt-8 text-xs text-[#78716C] leading-relaxed">
        <strong>Disclaimer:</strong> Size charts are approximate and based on standard brand guidelines. Sizes may vary by style, cut, and individual item. Always verify with {brand.name}'s official size chart before purchasing. TrueFitGuide is not affiliated with {brand.name}.
      </div>

      <SponsorBanner />
      <ClickBankBanner />
      <AdSensePlaceholder />
    </div>
  );
}
